import { authToken } from "@/queries/client";
import { API_ENDPOINT } from "@/lib/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Chat API client using raw fetch.
 *
 * NOTE: The AI chat endpoints are not yet in the OpenAPI schema (openapi.d.ts).
 * Once the backend schema is regenerated and the frontend types are updated,
 * these should be migrated to use the typed `client.get().GET/POST/DELETE()` pattern
 * like other query files (e.g., flights.ts, team.ts).
 *
 * To regenerate types after AI chat endpoints are stable:
 *   npx openapi-typescript <api-url>/api/openapi.json -o src/lib/openapi.d.ts
 */

// Types
export interface ChatMessage {
  role: "user" | "assistant" | "system" | "function";
  content: string;
}

export interface UIContext {
  current_page?: string;
  active_store_id?: number;
  active_brand_id?: number;
}
// active_concourse was removed to stay in step with the backend's UIContext
// schema, which dropped its terminal/concourse selection fields. A terminal or
// concourse is not an access boundary — the roles are airport, brand and
// retail_all — so which page the user navigated to must not narrow the scope of
// the answer. The backend ignores unknown keys, so sending one here would fail
// silently rather than error. Scope comes from the conversation; current_page
// still reaches the model as information.

export interface FunctionCall {
  function: string;
  arguments: Record<string, unknown>;
  result?: Record<string, unknown>;
}

export interface ChatResponse {
  response: string;
  session_id: number;
  function_calls: FunctionCall[];
}

export interface StreamEvent {
  type: "chunk" | "function_calls" | "done" | "error" | "correction";
  content?: string;
  data?: FunctionCall[];
  session_id?: number;
  debug_trace?: Array<{
    function_name: string;
    trace_type?: string;
    summary_key?: string;
    cache_hit?: boolean;
    precompute_metadata?: Record<string, unknown>;
    precompute_payload?: Record<string, unknown>;
    fallback_used?: string;
    tool_params?: Record<string, unknown>;
    data_provenance?: Record<string, unknown>;
  }>;
  message?: string;
}

export interface ChatSession {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  messages?: Array<{
    id: number;
    role: string;
    content: string;
    function_name?: string;
    created_at: string;
  }>;
}

export interface ChatUsage {
  period: string;
  sessions: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
}

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = authToken.get();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Helper for fetch with error handling
async function chatFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_ENDPOINT}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed: ${response.status}`);
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// API Hooks

/**
 * Send a chat message and get AI response
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messages,
      sessionId,
    }: {
      messages: ChatMessage[];
      sessionId?: number;
    }): Promise<ChatResponse> => {
      return chatFetch<ChatResponse>("/api/ai/chat", {
        method: "POST",
        body: JSON.stringify({
          messages: messages.map((m) => ({ role: m.role, content: m.content })),
          session_id: sessionId,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });
}

/**
 * List all chat sessions for the current user
 */
export function useChatSessions() {
  return useQuery({
    queryKey: ["chat-sessions"],
    queryFn: async () => {
      return chatFetch<ChatSession[]>("/api/ai/chat/sessions");
    },
  });
}

/**
 * Get a specific chat session with all messages
 */
export function useChatSession(sessionId: number | null) {
  return useQuery({
    queryKey: ["chat-session", sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      return chatFetch<ChatSession>(`/api/ai/chat/sessions/${sessionId}`);
    },
    // Don't retry on 404 — session was deleted or never existed.
    // Retrying only delays the UI clearing its stale state.
    retry: (failureCount, error) => {
      if ((error as Error).message?.includes("404")) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Create a new chat session
 */
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (title?: string): Promise<ChatSession> => {
      return chatFetch<ChatSession>("/api/ai/chat/sessions", {
        method: "POST",
        body: JSON.stringify({ title: title || "" }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });
}

/**
 * Delete a chat session
 */
export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: number): Promise<void> => {
      return chatFetch<void>(`/api/ai/chat/sessions/${sessionId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });
}

/**
 * Get token usage statistics
 */
export function useChatUsage() {
  return useQuery({
    queryKey: ["chat-usage"],
    queryFn: async () => {
      return chatFetch<ChatUsage>("/api/ai/chat/usage");
    },
  });
}

/**
 * Health check for chat service
 */
export function useChatHealth() {
  return useQuery({
    queryKey: ["chat-health"],
    queryFn: async () => {
      return chatFetch<{ status: string; airport: string }>(
        "/api/ai/chat/health"
      );
    },
    retry: 1,
  });
}

/**
 * Stream a chat message and get AI response progressively.
 * Returns an async generator that yields chunks of text.
 */
export async function* streamChatMessage({
  messages,
  sessionId,
  signal,
  uiContext,
}: {
  messages: ChatMessage[];
  sessionId?: number;
  signal?: AbortSignal;
  uiContext?: UIContext;
}): AsyncGenerator<StreamEvent> {
  const token = authToken.get();

  const response = await fetch(`${API_ENDPOINT}/api/ai/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
      session_id: sessionId,
      ui_context: uiContext,
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Stream failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const event: StreamEvent = JSON.parse(line.slice(6));
            yield event;
          } catch {
            // Skip malformed JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
